import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Settings, Save, RotateCcw, Building2, Globe, Clock, Target, Users, FileText, Bell } from 'lucide-react';
import { MaterialitySettings as MaterialitySettingsType } from '@/types/materiality';
import { materialityService } from '@/utils/materialityService';

interface MaterialitySettingsProps {}

export const MaterialitySettings: React.FC<MaterialitySettingsProps> = () => {
  const [settings, setSettings] = useState<MaterialitySettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching materiality settings...');
        const settingsData = await materialityService.getMaterialitySettings();
        console.log('Received materiality settings:', settingsData);
        setSettings(settingsData);
      } catch (error) {
        console.error('Error fetching materiality settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSave = async () => {
    if (!settings) return;
    
    setSaving(true);
    try {
      // Update settings in database
      const updatedSettings = await materialityService.updateMaterialitySettings(settings.id, settings);
      if (updatedSettings) {
        setSettings(updatedSettings);
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset to original values - would need to refetch from database
    setHasChanges(false);
  };

  const handleSettingChange = (key: keyof MaterialitySettingsType, value: any) => {
    if (!settings) return;
    
    setSettings(prev => {
      if (!prev) return prev;
      return { ...prev, [key]: value };
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Materiality Settings
            </CardTitle>
            <CardDescription>
              Configure materiality assessment parameters and preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading settings...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Materiality Settings
            </CardTitle>
            <CardDescription>
              Configure materiality assessment parameters and preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No settings found. Please initialize default settings.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Materiality Settings
              </CardTitle>
              <CardDescription>
                Configure materiality assessment parameters and preferences
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Organization Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Organization Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="organization">Organization Name</Label>
                <Input
                  id="organization"
                  value={settings.organization}
                  onChange={(e) => handleSettingChange('organization', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="industry">Industry</Label>
                <Select value={settings.industry} onValueChange={(value) => handleSettingChange('industry', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="Financial Services">Financial Services</SelectItem>
                    <SelectItem value="Energy">Energy</SelectItem>
                    <SelectItem value="Healthcare">Healthcare</SelectItem>
                    <SelectItem value="Retail">Retail</SelectItem>
                    <SelectItem value="Transportation">Transportation</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sector">Sector</Label>
                <Input
                  id="sector"
                  value={settings.sector}
                  onChange={(e) => handleSettingChange('sector', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="region">Region</Label>
                <Select value={settings.region} onValueChange={(value) => handleSettingChange('region', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="North America">North America</SelectItem>
                    <SelectItem value="Europe">Europe</SelectItem>
                    <SelectItem value="Asia Pacific">Asia Pacific</SelectItem>
                    <SelectItem value="Latin America">Latin America</SelectItem>
                    <SelectItem value="Africa">Africa</SelectItem>
                    <SelectItem value="Global">Global</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Assessment Parameters */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Assessment Parameters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="assessmentFrequency">Assessment Frequency</Label>
                <Select value={settings.assessmentFrequency} onValueChange={(value) => handleSettingChange('assessmentFrequency', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="biannual">Biannual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="stakeholderEngagementMethod">Stakeholder Engagement Method</Label>
                <Select value={settings.stakeholderEngagementMethod} onValueChange={(value) => handleSettingChange('stakeholderEngagementMethod', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="survey">Survey</SelectItem>
                    <SelectItem value="interview">Interview</SelectItem>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="materialityThreshold">Materiality Threshold (%)</Label>
                <Input
                  id="materialityThreshold"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.materialityThreshold}
                  onChange={(e) => handleSettingChange('materialityThreshold', parseInt(e.target.value))}
                />
              </div>
              <div>
                <Label htmlFor="maxMaterialTopics">Maximum Material Topics</Label>
                <Input
                  id="maxMaterialTopics"
                  type="number"
                  min="1"
                  max="50"
                  value={settings.maxMaterialTopics}
                  onChange={(e) => handleSettingChange('maxMaterialTopics', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Feature Toggles */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Feature Toggles</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableIndustryBenchmarking">Industry Benchmarking</Label>
                  <p className="text-sm text-gray-500">Compare against industry peers</p>
                </div>
                <Switch
                  id="enableIndustryBenchmarking"
                  checked={settings.enableIndustryBenchmarking}
                  onCheckedChange={(checked) => handleSettingChange('enableIndustryBenchmarking', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableStakeholderMapping">Stakeholder Mapping</Label>
                  <p className="text-sm text-gray-500">Enable stakeholder relationship mapping</p>
                </div>
                <Switch
                  id="enableStakeholderMapping"
                  checked={settings.enableStakeholderMapping}
                  onCheckedChange={(checked) => handleSettingChange('enableStakeholderMapping', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableAutomatedReporting">Automated Reporting</Label>
                  <p className="text-sm text-gray-500">Generate automated reports</p>
                </div>
                <Switch
                  id="enableAutomatedReporting"
                  checked={settings.enableAutomatedReporting}
                  onCheckedChange={(checked) => handleSettingChange('enableAutomatedReporting', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="enableNotifications">Notifications</Label>
                  <p className="text-sm text-gray-500">Enable email and in-app notifications</p>
                </div>
                <Switch
                  id="enableNotifications"
                  checked={settings.enableNotifications}
                  onCheckedChange={(checked) => handleSettingChange('enableNotifications', checked)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Regional Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Regional Settings</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="defaultLanguage">Default Language</Label>
                <Select value={settings.defaultLanguage} onValueChange={(value) => handleSettingChange('defaultLanguage', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="zh">Chinese</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={settings.timezone} onValueChange={(value) => handleSettingChange('timezone', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="EST">Eastern Time</SelectItem>
                    <SelectItem value="PST">Pacific Time</SelectItem>
                    <SelectItem value="CET">Central European Time</SelectItem>
                    <SelectItem value="JST">Japan Standard Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={settings.currency} onValueChange={(value) => handleSettingChange('currency', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="JPY">JPY</SelectItem>
                    <SelectItem value="CNY">CNY</SelectItem>
                    <SelectItem value="NGN">NGN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Custom Topics */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Custom Topics</h3>
            </div>
            <div>
              <Label htmlFor="customTopics">Custom Material Topics (one per line)</Label>
              <Textarea
                id="customTopics"
                value={settings.customTopics.join('\n')}
                onChange={(e) => handleSettingChange('customTopics', e.target.value.split('\n').filter(topic => topic.trim()))}
                placeholder="Enter custom material topics, one per line"
                rows={4}
              />
            </div>
            <div>
              <Label htmlFor="excludedTopics">Excluded Topics (one per line)</Label>
              <Textarea
                id="excludedTopics"
                value={settings.excludedTopics.join('\n')}
                onChange={(e) => handleSettingChange('excludedTopics', e.target.value.split('\n').filter(topic => topic.trim()))}
                placeholder="Enter topics to exclude, one per line"
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Reporting Standards */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <h3 className="text-lg font-semibold">Reporting Standards</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['GRI', 'SASB', 'TCFD', 'CDP', 'ISO 26000', 'UNGC', 'SDGs', 'Other'].map((standard) => (
                <div key={standard} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={standard}
                    checked={settings.reportingStandards.includes(standard)}
                    onChange={(e) => {
                      const currentStandards = settings.reportingStandards;
                      const newStandards = e.target.checked
                        ? [...currentStandards, standard]
                        : currentStandards.filter(s => s !== standard);
                      handleSettingChange('reportingStandards', newStandards);
                    }}
                    className="rounded"
                  />
                  <Label htmlFor={standard} className="text-sm">{standard}</Label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Last Updated */}
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Last updated: {new Date(settings.lastUpdated).toLocaleDateString()}</span>
            </div>
            {hasChanges && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Bell className="h-3 w-3" />
                Unsaved changes
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 